/** @jsxImportSource @emotion/react */
import type { MapLayer, MapLayerType } from '@app/types/layerTypes'
import { DialogData } from '../AddLayersDialog.types'
import { AssetLayersForm } from '../AssetLayersForm'
import { HazardLayersForm } from '../HazardLayersForm'
import { InformationLayersForm } from '../InformationLayersForm'
import { LiveLayersForm } from '@app/modules/Live/components/Live.LayersForm'
import { addLayersSetupSection, addLayersTabsContainer } from './LayerSetup.styles'
import { usePreferences } from '@app/hooks/usePreferences'
import { Section } from '@app/components/Atoms/Section'
import { Tabs } from '@app/components/Molecules/Tabs'
import { LiveLayer } from '@app/modules/Live/Live.types'
import { useAtom } from 'jotai'
import { showAddLayersModalTabAtom } from '@app/risk-migration/atoms/riskMap.atoms'
export interface LayerSetupProps {
  dialogData: DialogData
  selectedHazards: MapLayer[]
  selectedAssets: MapLayer[]
  selectedInformation: MapLayer[]
  selectedLive: LiveLayer[]
  onSelectHazardLayer: (selectedHazard: MapLayer) => void
  onSelectInformationLayer: (selectedInformation: MapLayer) => void
  onSelectLiveLayer: (selectedLiveLayer: LiveLayer) => void
  onSelectAssets: (selectedAsset: MapLayer[]) => void
  onRemoveLayer: (layer: MapLayer, type: MapLayerType) => void
  liveData?: boolean
}

export const LayerSetup = ({
  dialogData,
  selectedHazards,
  selectedAssets,
  selectedInformation,
  selectedLive,
  onSelectHazardLayer,
  onSelectInformationLayer,
  onSelectLiveLayer,
  onSelectAssets,
  onRemoveLayer,
  liveData = false,
}: LayerSetupProps) => {
  const { term_preference } = usePreferences()
  const [showAddLayersModalTab, setShowAddLayersModalTab] = useAtom(showAddLayersModalTabAtom)
  const { assetDialogData, hazardDialogData } = dialogData
  let { informationDialogData } = dialogData

  // allows backward compatibility with old informationDialogData
  if (Array.isArray(informationDialogData)) {
    informationDialogData = {
      Ungrouped: {
        title: 'Contextual Layers',
        icon: 'Tree',
        groups: [
          {
            groupName: 'Ungrouped',
            icon: 'Tree',
            layers: [...informationDialogData],
          },
        ],
      },
    }
  }

  const tabOrder = liveData
    ? ['live', 'live-element', 'hazard', 'contextual']
    : ['element', 'hazard', 'contextual']

  const tabIndex = Math.max(0, tabOrder.indexOf(showAddLayersModalTab))

  const handleTabChange = (index: number) => {
    setShowAddLayersModalTab(tabOrder[index])
  }

  return (
    <Section css={addLayersSetupSection} className="tutorial-addlayers-panel">
      <Tabs
        css={addLayersTabsContainer}
        tabIndex={tabIndex}
        handleChangeTab={handleTabChange}
        tabs={[
          {
            show: liveData,
            label: 'LIVE DATA',
            content: (
              <LiveLayersForm
                onSelectLiveLayer={onSelectLiveLayer}
                selectedLive={selectedLive}
                onRemoveLayer={onRemoveLayer}
              />
            ),
          },
          {
            label: 'ELEMENTS',
            content: assetDialogData ? (
              <AssetLayersForm
                assetDialogData={assetDialogData}
                selectedAssets={selectedAssets}
                selectedHazards={selectedHazards}
                onSelectAssets={onSelectAssets}
                onRemoveLayer={onRemoveLayer}
              />
            ) : null,
          },
          {
            label: term_preference.hazard ? 'HAZARDS' : 'RISK SOURCES',
            content: dialogData.hazardDialogData ? (
              <HazardLayersForm
                selectedHazards={selectedHazards}
                selectedAssets={selectedAssets}
                hazardDialogData={hazardDialogData}
                onSelectHazardLayer={onSelectHazardLayer}
                onRemoveLayer={onRemoveLayer}
              />
            ) : null,
          },
          {
            label: 'CONTEXTUAL',
            content: informationDialogData ? (
              <InformationLayersForm
                selectedInformation={selectedInformation}
                informationDialogData={informationDialogData}
                onSelectInformationLayer={onSelectInformationLayer}
                onRemoveLayer={onRemoveLayer}
              />
            ) : null,
          },
        ]}
      />
      {/* <Typography variant="body1" css={addLayersSectionTitle({ theme })}>
        Layer Setup
      </Typography> */}
    </Section>
  )
}
